import React, { FC, useState } from "react";

// UI
import { Box } from "@UI/Components/Box/Box";
import { Flex } from "@UI/Components/Flex/Flex";
import { Text } from "@UI/Components/Text/Text";
import { Button } from "@UI/Components/Button/Button";
import { VStack, HStack } from "@UI/Components/Stack/Stack";
import { Container } from "@UI/Components/Container/Container";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { ShieldCheck } from "@UI/Components/Icons/ShieldCheck";
import { Coins } from "@UI/Components/Icons/Coins";
import { StarRegular } from "@UI/Components/Icons/StarRegular";
import { Ban } from "@UI/Components/Icons/Ban";
import {
  Wizard,
  WizardContent,
  WizardHeader,
  WizardNav,
} from "@Shared/Components/Layouts/Wizard/Wizard";
import { BackLink } from "@Shared/Components/BackLink/BackLink";
import { FAQSection } from "@modules/CustomerSupport/Components/FAQSection/FAQSection";
import useMediaQuery from "@UI/hooks/useMediaQuery";

// i18n
import messages from "./messages";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { FormattedNumber } from "@I18n/Components/FormattedNumber/FormattedNumber";

// hooks
import { useSubscriptionPlans } from "@modules/Subscriptions/hooks/useSubscriptionPlans";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { trackEvent } from "@Libs/GrowthBook/services/growthbook";

// types
import { ISubscriptionPlan } from "@modules/Subscriptions/types";

const PINK_500 = "#FF478F";
const FA_ICON_STYLE = { color: PINK_500, fontSize: "14px", flexShrink: 0 } as const;
const GRADIENT = "linear-gradient(96.51deg, #FF478F 0%, #FF9800 99.87%)";

interface IProps {
  onBack: () => void;
  onSelect: (plan: ISubscriptionPlan) => void;
  isDisabled?: boolean;
  initialPeriod?: "quarter" | "year";
  serviceOfferId?: number;
}

export const ChooseSubscriptionPlanActivationRate: FC<IProps> = ({
  onBack,
  onSelect,
  isDisabled = false,
  initialPeriod = "quarter",
  serviceOfferId,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<"quarter" | "year">(
    initialPeriod
  );
  const { subscriptionPlans, isLoading } = useSubscriptionPlans();
  const { isMobile } = useMediaQuery();
  const { currentUser } = useCurrentUser();

  const baseEventProps = {
    contractId: serviceOfferId,
    clientId: currentUser?.id,
    clientEmail: currentUser?.email,
    isSubscribed: currentUser?.subscribed ?? false,
  };

  const handlePeriodToggle = (period: "quarter" | "year") => {
    setSelectedPeriod(period);
    // activation_rate experiment events — safe to remove once experiment is concluded
    trackEvent("subscription_period_toggled", { ...baseEventProps, period });
  };

  if (isLoading) return <LoadingScreen />;

  const quarterlyPlan = subscriptionPlans.find((p) => p.period === "quarter");
  const yearlyPlan = subscriptionPlans.find((p) => p.period === "year");
  const selectedPlan =
    selectedPeriod === "quarter" ? quarterlyPlan : yearlyPlan;

  const yearlyDiscountPct =
    quarterlyPlan && yearlyPlan
      ? Math.round(
          (1 - yearlyPlan.priceCents / (quarterlyPlan.priceCents * 4)) * 100
        )
      : 35;

  if (!selectedPlan) return null;

  return (
    <>
      <Wizard>
        <WizardNav>
          <BackLink onClick={onBack} />
        </WizardNav>
        <WizardHeader>
          <Flex justifyContent="center">
            <Text
              as="h3"
              fontSize={{ base: "2xl", lg: "4xl" }}
              fontWeight="normal"
              fontFamily="'Jornada Sans Book', sans-serif"
              textAlign="center"
            >
              <FormattedMessage {...messages.title} />
            </Text>
          </Flex>
        </WizardHeader>
        <WizardContent>
          <VStack spacing={{ base: "4", lg: "8" }} alignItems="stretch">
            {/* Centered content column */}
            <VStack
              spacing={{ base: "5", lg: "8" }}
              alignSelf={{ lg: "center" }}
              w={{ base: "full", lg: "420px" }}
              alignItems="stretch"
            >
              {/* Period toggle — only shown when both plans exist */}
              {quarterlyPlan && yearlyPlan && (
                <Box
                  position="relative"
                  h="50px"
                  borderRadius="full"
                  border="1px solid #EADDD1"
                  bg="#F2EAE2"
                  p="4px"
                >
                  {/* Sliding gradient indicator */}
                  <Box
                    position="absolute"
                    top="4px"
                    left={
                      selectedPeriod === "quarter" ? "4px" : "calc(50% + 0px)"
                    }
                    w="calc(50% - 4px)"
                    h="calc(100% - 8px)"
                    borderRadius="full"
                    border="1px solid white"
                    style={{ background: GRADIENT }}
                    transition="left 0.25s ease"
                    pointerEvents="none"
                  />
                  {/* Button row */}
                  <Flex h="full" position="relative" zIndex={1}>
                    <Box
                      as="button"
                      flex="1"
                      borderRadius="full"
                      fontSize="md"
                      fontWeight="normal"
                      color={
                        selectedPeriod === "quarter" ? "white" : "#666666"
                      }
                      transition="color 0.25s ease"
                      onClick={() => handlePeriodToggle("quarter")}
                    >
                      <FormattedMessage {...messages.quarterly} />
                    </Box>
                    <Box
                      as="button"
                      flex="1"
                      borderRadius="full"
                      fontSize="md"
                      fontWeight="normal"
                      color={selectedPeriod === "year" ? "white" : "#666666"}
                      transition="color 0.25s ease"
                      onClick={() => handlePeriodToggle("year")}
                    >
                      <FormattedMessage {...messages.yearly} />{" "}
                      <Text as="span" fontSize="sm">
                        (-{yearlyDiscountPct}%)
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              )}

              {/* Summary card */}
              <Box
                bg="white"
                borderRadius="12px"
                border="1px solid"
                borderColor="gray.200"
                p="4"
                boxShadow="0px 0px 3px #DEDEDE"
                sx={{ "& p": { mb: 0 } }}
              >
                {/* Plan header row */}
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  mb="3"
                >
                  <Text fontWeight="bold" color="gray.800">
                    <FormattedMessage
                      {...(selectedPeriod === "quarter"
                        ? messages.planTitleQuarterly
                        : messages.planTitleYearly)}
                    />
                  </Text>
                  <Text fontWeight="bold" color="gray.800" flexShrink={0}>
                    <FormattedNumber
                      value={selectedPlan.priceCents / 100}
                      style="currency"
                      currency={selectedPlan.priceCurrency}
                      minimumFractionDigits={0}
                    />
                  </Text>
                </Flex>

                {/* Included label */}
                <Text fontSize="sm" color="gray.500" mb="0" pb="4">
                  <FormattedMessage {...messages.includedOnSubscription} />
                </Text>

                {/* Benefits */}
                <VStack spacing="3" alignItems="stretch" mb="3">
                  <HStack spacing="2">
                    <ShieldCheck style={FA_ICON_STYLE} />
                    <Text fontSize="sm" color="gray.700">
                      <FormattedMessage {...messages.jobInsurance} />
                    </Text>
                  </HStack>
                  <HStack spacing="2">
                    <Coins style={FA_ICON_STYLE} />
                    <Text fontSize="sm" color="gray.700">
                      <FormattedMessage
                        {...messages.noServiceFee}
                        values={{
                          bold: ((chunks: React.ReactNode) => (
                            <Text as="span" fontWeight="bold" color="pink.500">
                              {chunks}
                            </Text>
                          )) as unknown as () => React.ReactNode,
                        }}
                      />
                    </Text>
                  </HStack>
                  <HStack spacing="2">
                    <StarRegular style={FA_ICON_STYLE} />
                    <Text fontSize="sm" color="gray.700">
                      <FormattedMessage {...messages.satisfiedOrRedone} />
                    </Text>
                  </HStack>
                  <HStack spacing="2">
                    <Ban style={FA_ICON_STYLE} />
                    <Text fontSize="sm" color="gray.700">
                      <FormattedMessage {...messages.cancelForFree} />
                    </Text>
                  </HStack>
                </VStack>

                {/* Divider */}
                <Box h="1px" bg="gray.200" my="3" />

                {/* Total row */}
                <Flex justifyContent="space-between" alignItems="center">
                  <Text fontWeight="bold" color="gray.800">
                    <FormattedMessage {...messages.total} />
                  </Text>
                  <Text fontWeight="bold" color="gray.800" flexShrink={0}>
                    <FormattedNumber
                      value={selectedPlan.priceCents / 100}
                      style="currency"
                      currency={selectedPlan.priceCurrency}
                      minimumFractionDigits={0}
                    />
                  </Text>
                </Flex>
              </Box>

              {/* Cancel note */}
              <Text fontSize="xs" color="gray.500" textAlign="center">
                <FormattedMessage {...messages.cancelRenewal} />
              </Text>

              {/* CTA */}
              <Button
                colorScheme="seaGreen"
                size="lg"
                w="full"
                isDisabled={isDisabled}
                onClick={() => {
                  // activation_rate experiment events — safe to remove once experiment is concluded
                  trackEvent("subscription_plan_selected", {
                    ...baseEventProps,
                    period: selectedPeriod,
                    planId: selectedPlan.id,
                    planPriceCents: selectedPlan.priceCents,
                    planCurrency: selectedPlan.priceCurrency,
                  });
                  onSelect(selectedPlan);
                }}
              >
                <FormattedMessage {...messages.continueToPayment} />
              </Button>
            </VStack>

            {isMobile && (
              <Box marginTop="10">
                <FAQSection />
              </Box>
            )}
          </VStack>
        </WizardContent>
      </Wizard>
      {!isMobile && (
        <Container
          marginY={[10, 10, 20]}
          maxW={["container.lg", "container.md", "container.lg"]}
        >
          <FAQSection />
        </Container>
      )}
    </>
  );
};
